import React, { useState, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { api } from '@/services/api';
import { Folder, FileAudio, FileImage, ArrowUp, Loader2, AlertCircle, X, Upload, Trash2 } from 'lucide-react';

interface FtpFilePickerProps {
  value: string;
  onChange: (path: string) => void;
  label: string;
  placeholder?: string;
  fileFilter?: string; // e.g., ".mp3"
  disabled?: boolean;
  basePath?: string; // Starting directory for file browser
  compact?: boolean; // Compact single-row display mode
  companyId?: string; // Override company for multi-company users
}

interface FtpFile {
  name: string;
  path: string;
  size: number;
}

interface FtpDirectory {
  name: string;
  path: string;
}

interface BrowseResponse {
  success: boolean;
  currentPath: string;
  directories: FtpDirectory[];
  files: FtpFile[];
  error?: string;
}

interface UploadResponse {
  success: boolean;
  path?: string;
  filename?: string;
  size?: number;
  error?: string;
}

interface DeleteResponse {
  success: boolean;
  path?: string;
  error?: string;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const FtpFilePicker: React.FC<FtpFilePickerProps> = ({
  value,
  onChange,
  label,
  placeholder = 'Select an audio file...',
  fileFilter = '.mp3',
  disabled = false,
  basePath,
  compact = false,
  companyId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentPath, setCurrentPath] = useState('/');
  const [directories, setDirectories] = useState<FtpDirectory[]>([]);
  const [files, setFiles] = useState<FtpFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null); // Path being deleted
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null); // Path awaiting delete confirmation
  const fileInputRef = useRef<HTMLInputElement>(null);

  const browseDirectory = useCallback(async (path: string) => {
    setLoading(true);
    setError(null);

    try {
      // Parse comma-separated extensions into array
      const extensions = fileFilter
        .split(',')
        .map(ext => ext.trim().toLowerCase())
        .filter(ext => ext.length > 0);

      // Use first extension for API call (API only handles single filter)
      // We'll filter results client-side for multiple extensions
      const response = await api.post<BrowseResponse>('/ftp/browse', {
        path,
        companyId,
        // Don't pass fileFilter to API - we'll filter client-side for multiple extensions
      });

      if (response.success && response.data) {
        if (response.data.success) {
          setCurrentPath(response.data.currentPath);
          setDirectories(response.data.directories);

          // Filter files client-side to support multiple extensions
          const filteredFiles = response.data.files.filter(file => {
            const fileName = file.name.toLowerCase();
            return extensions.some(ext => fileName.endsWith(ext));
          });
          setFiles(filteredFiles);
        } else {
          setError(response.data.error || 'Failed to browse directory');
        }
      } else {
        setError(response.error?.message || 'Failed to browse directory');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to browse directory');
    } finally {
      setLoading(false);
    }
  }, [fileFilter, companyId]);

  const handleOpenDialog = () => {
    setIsOpen(true);
    setSelectedFile(value || null);
    // Start from parent dir of current value, or basePath, or root
    const startPath = value
      ? value.substring(0, value.lastIndexOf('/')) || basePath || '/'
      : basePath || '/';
    browseDirectory(startPath);
  };

  const handleNavigateUp = () => {
    if (currentPath === '/') return;
    const parentPath = currentPath.substring(0, currentPath.lastIndexOf('/')) || '/';
    browseDirectory(parentPath);
  };

  const handleNavigateToDirectory = (dirPath: string) => {
    browseDirectory(dirPath);
  };

  const handleSelectFile = (filePath: string) => {
    setSelectedFile(filePath);
  };

  const handleConfirm = () => {
    if (selectedFile) {
      onChange(selectedFile);
    }
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange('');
  };

  const handleClearAndClose = () => {
    onChange('');
    setSelectedFile(null);
    setIsOpen(false);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (15MB max)
    const maxSize = 15 * 1024 * 1024;
    if (file.size > maxSize) {
      setError(`File too large. Maximum size is 15MB, got ${(file.size / 1024 / 1024).toFixed(1)}MB`);
      return;
    }

    // Validate file type based on fileFilter
    const extensions = fileFilter
      .split(',')
      .map(ext => ext.trim().toLowerCase())
      .filter(ext => ext.length > 0);
    const fileName = file.name.toLowerCase();
    const validExtension = extensions.some(ext => fileName.endsWith(ext));
    if (!validExtension) {
      setError(`Invalid file type. Allowed: ${extensions.join(', ')}`);
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('directory', currentPath);

      // Build URL with optional companyId query param
      let uploadUrl = '/podcast/settings/upload';
      if (companyId) {
        uploadUrl += `?companyId=${encodeURIComponent(companyId)}`;
      }

      const response = await api.upload<UploadResponse>(uploadUrl, formData);

      if (response.success && response.data?.success) {
        // Refresh directory to show new file
        await browseDirectory(currentPath);
        // Auto-select the uploaded file
        if (response.data.path) {
          setSelectedFile(response.data.path);
        }
      } else {
        setError(response.data?.error || response.error?.message || 'Upload failed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      // Reset file input so same file can be uploaded again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteClick = (filePath: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent selecting the file when clicking delete
    setDeleteConfirm(filePath); // Open confirmation dialog
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;

    const filePath = deleteConfirm;
    setDeleteConfirm(null); // Close confirmation dialog
    setDeleting(filePath);
    setError(null);

    try {
      // Build URL with path and optional companyId
      let deleteUrl = `/podcast/settings/file?path=${encodeURIComponent(filePath)}`;
      if (companyId) {
        deleteUrl += `&companyId=${encodeURIComponent(companyId)}`;
      }

      const response = await api.delete<DeleteResponse>(deleteUrl);

      if (response.success && response.data?.success) {
        // Refresh directory to reflect deletion
        await browseDirectory(currentPath);
        // Clear selection if deleted file was selected
        if (selectedFile === filePath) {
          setSelectedFile(null);
        }
      } else {
        setError(response.data?.error || response.error?.message || 'Delete failed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setDeleting(null);
    }
  };

  // Get accept string for file input from fileFilter
  const acceptTypes = fileFilter
    .split(',')
    .map(ext => ext.trim())
    .filter(ext => ext.length > 0)
    .join(',');

  // Extract filename from path for compact display
  const displayValue = value ? value.split('/').pop() : null;

  // Compact mode: single row with status indicator
  if (compact) {
    return (
      <>
        <div className="flex items-center gap-3 py-2 px-1 hover:bg-muted/50 rounded-md group">
          {/* Status indicator */}
          <div
            className={`w-2 h-2 rounded-full shrink-0 ${
              value ? 'bg-green-500' : 'bg-muted-foreground/30'
            }`}
          />
          {/* Label */}
          <span className="w-28 text-sm font-medium shrink-0">{label}</span>
          {/* Value */}
          <span
            className={`flex-1 text-sm truncate ${
              value ? 'text-foreground font-mono' : 'text-muted-foreground italic'
            }`}
          >
            {displayValue || 'Not set'}
          </span>
          {/* Clear button */}
          {value && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          {/* Browse button */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleOpenDialog}
            disabled={disabled}
          >
            Browse
          </Button>
        </div>

        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
            <DialogHeader>
              <DialogTitle>Select {label}</DialogTitle>
              <DialogDescription>
                Browse and select an audio file for {label.toLowerCase()}
              </DialogDescription>
            </DialogHeader>

            {/* Current path and navigation */}
            <div className="flex items-center gap-2 py-2 border-b">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleNavigateUp}
                disabled={currentPath === '/' || loading}
              >
                <ArrowUp className="h-4 w-4 mr-1" />
                Up
              </Button>
              <span className="text-sm text-muted-foreground truncate flex-1">
                {currentPath}
              </span>
            </div>

            {/* Error message */}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-md">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {/* File browser */}
            <div className="flex-1 overflow-auto min-h-[300px]">
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <div className="space-y-1">
                  {/* Directories */}
                  {directories.map((dir) => (
                    <button
                      key={dir.path}
                      type="button"
                      onClick={() => handleNavigateToDirectory(dir.path)}
                      className="w-full flex items-center gap-3 px-3 py-2 hover:bg-accent rounded-md text-left"
                    >
                      <Folder className="h-5 w-5 text-blue-500" />
                      <span className="flex-1 truncate">{dir.name}</span>
                    </button>
                  ))}

                  {/* Files */}
                  {files.map((file) => {
                    const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(file.name);
                    const isDeleting = deleting === file.path;
                    return (
                      <div
                        key={file.path}
                        className={`w-full flex items-center gap-3 px-3 py-2 hover:bg-accent rounded-md group ${
                          selectedFile === file.path ? 'bg-accent ring-2 ring-primary' : ''
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => handleSelectFile(file.path)}
                          className="flex items-center gap-3 flex-1 text-left"
                          disabled={isDeleting}
                        >
                          {isImage ? (
                            <FileImage className="h-5 w-5 text-purple-500" />
                          ) : (
                            <FileAudio className="h-5 w-5 text-green-500" />
                          )}
                          <span className="flex-1 truncate">{file.name}</span>
                          <span className="text-sm text-muted-foreground">
                            {formatFileSize(file.size)}
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={(e) => handleDeleteClick(file.path, e)}
                          disabled={isDeleting || uploading}
                          className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity disabled:opacity-50"
                          title="Delete file"
                        >
                          {isDeleting ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    );
                  })}

                  {/* Empty state */}
                  {directories.length === 0 && files.length === 0 && !loading && (
                    <div className="text-center py-8 text-muted-foreground">
                      No files or directories found
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Selected file preview */}
            {selectedFile && (
              <div className="py-2 border-t">
                <p className="text-sm">
                  <span className="text-muted-foreground">Selected: </span>
                  <span className="font-medium">{selectedFile}</span>
                </p>
              </div>
            )}

            <DialogFooter className="gap-2 sm:gap-0">
              {/* Hidden file input for upload */}
              <input
                ref={fileInputRef}
                type="file"
                accept={acceptTypes}
                onChange={handleFileUpload}
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleUploadClick}
                disabled={uploading || loading || deleting !== null}
                className="mr-auto"
              >
                {uploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload
                  </>
                )}
              </Button>
              {value && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleClearAndClose}
                  className="text-muted-foreground hover:text-destructive"
                >
                  Clear
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleConfirm}
                disabled={!selectedFile || deleting !== null}
              >
                Select
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete confirmation dialog */}
        <Dialog open={deleteConfirm !== null} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" />
                Delete File
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete{' '}
                <span className="font-medium text-foreground">
                  {deleteConfirm?.split('/').pop()}
                </span>
                ? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDeleteConfirm(null)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={handleDeleteConfirm}
              >
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Standard mode: label + input + button
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Input
            value={value}
            readOnly
            placeholder={placeholder}
            disabled={disabled}
            className="pr-8"
          />
          {value && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={handleOpenDialog}
          disabled={disabled}
        >
          Browse
        </Button>
      </div>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Select {label}</DialogTitle>
            <DialogDescription>
              Browse and select an audio file for {label.toLowerCase()}
            </DialogDescription>
          </DialogHeader>

          {/* Current path and navigation */}
          <div className="flex items-center gap-2 py-2 border-b">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleNavigateUp}
              disabled={currentPath === '/' || loading}
            >
              <ArrowUp className="h-4 w-4 mr-1" />
              Up
            </Button>
            <span className="text-sm text-muted-foreground truncate flex-1">
              {currentPath}
            </span>
          </div>

          {/* Error message */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-md">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* File browser */}
          <div className="flex-1 overflow-auto min-h-[300px]">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="space-y-1">
                {/* Directories */}
                {directories.map((dir) => (
                  <button
                    key={dir.path}
                    type="button"
                    onClick={() => handleNavigateToDirectory(dir.path)}
                    className="w-full flex items-center gap-3 px-3 py-2 hover:bg-accent rounded-md text-left"
                  >
                    <Folder className="h-5 w-5 text-blue-500" />
                    <span className="flex-1 truncate">{dir.name}</span>
                  </button>
                ))}

                {/* Files */}
                {files.map((file) => {
                  const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(file.name);
                  const isDeleting = deleting === file.path;
                  return (
                    <div
                      key={file.path}
                      className={`w-full flex items-center gap-3 px-3 py-2 hover:bg-accent rounded-md group ${
                        selectedFile === file.path ? 'bg-accent ring-2 ring-primary' : ''
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => handleSelectFile(file.path)}
                        className="flex items-center gap-3 flex-1 text-left"
                        disabled={isDeleting}
                      >
                        {isImage ? (
                          <FileImage className="h-5 w-5 text-purple-500" />
                        ) : (
                          <FileAudio className="h-5 w-5 text-green-500" />
                        )}
                        <span className="flex-1 truncate">{file.name}</span>
                        <span className="text-sm text-muted-foreground">
                          {formatFileSize(file.size)}
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={(e) => handleDeleteClick(file.path, e)}
                        disabled={isDeleting || uploading}
                        className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity disabled:opacity-50"
                        title="Delete file"
                      >
                        {isDeleting ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  );
                })}

                {/* Empty state */}
                {directories.length === 0 && files.length === 0 && !loading && (
                  <div className="text-center py-8 text-muted-foreground">
                    No files or directories found
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Selected file preview */}
          {selectedFile && (
            <div className="py-2 border-t">
              <p className="text-sm">
                <span className="text-muted-foreground">Selected: </span>
                <span className="font-medium">{selectedFile}</span>
              </p>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            {/* Hidden file input for upload */}
            <input
              ref={fileInputRef}
              type="file"
              accept={acceptTypes}
              onChange={handleFileUpload}
              className="hidden"
            />
            <Button
              type="button"
              variant="outline"
              onClick={handleUploadClick}
              disabled={uploading || loading || deleting !== null}
              className="mr-auto"
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload
                </>
              )}
            </Button>
            {value && (
              <Button
                type="button"
                variant="ghost"
                onClick={handleClearAndClose}
                className="text-muted-foreground hover:text-destructive"
              >
                Clear
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleConfirm}
              disabled={!selectedFile || deleting !== null}
            >
              Select
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteConfirm !== null} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="h-5 w-5" />
              Delete File
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete{' '}
              <span className="font-medium text-foreground">
                {deleteConfirm?.split('/').pop()}
              </span>
              ? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDeleteConfirm}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FtpFilePicker;
